import { useAtomValue } from "jotai";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";

import { CardCategoryEnum, ElementEnum } from "@sorcery/db";
import { H3, cn } from "@sorcery/ui";

import { decklistJotai } from "~/state/deckJotai";
import type { DeckCardType } from "~/types/deck";
import { elementToImgMap } from "~/utils/element";
import { ManaCurveChart } from "./ManaCurveChart";

export const Stats = ({ size = "lg" }: { size?: "sm" | "lg" }) => {
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const decklist = useAtomValue(decklistJotai)!;
  const [showManaCurve, setShowManaCurve] = useState(false);

  const getSites = useCallback(
    (decklist: DeckCardType[]) =>
      decklist.filter((deckCard) => deckCard.card.guardian?.category === CardCategoryEnum.Site),
    [],
  );

  const getSpells = useCallback(
    (dlis: DeckCardType[]) =>
      dlis.filter((deckCard) => deckCard.card.guardian?.category === CardCategoryEnum.Spell),
    [],
  );

  // show mana curve if there are more than 3 spells or sites and we're on large mode
  useEffect(() => {
    setShowManaCurve(size === "lg" && getSpells(decklist).length + getSites(decklist).length > 3);
  }, [decklist, getSites, getSpells, size]);

  const getPipCount = useCallback((deckCard: DeckCardType[], element?: ElementEnum) => {
    return deckCard.reduce((a, c) => {
      const elementsToCheck = element
        ? [element]
        : [ElementEnum.Earth, ElementEnum.Fire, ElementEnum.Water, ElementEnum.Air];
      let pipCount = 0;
      elementsToCheck.forEach((e) => {
        pipCount += elementToThreshold(c, e);
      });
      return a + c.quantity * pipCount;
    }, 0);
  }, []);

  const hasCardsWithPip = useCallback(
    (deckCards: DeckCardType[], element: ElementEnum) =>
      !!deckCards.filter(
        (dc) =>
          dc.card.guardian?.category === CardCategoryEnum.Spell &&
          !!elementToThreshold(dc, element),
      ).length,
    [],
  );

  const spellPipRatio = useCallback(
    (deckCards: DeckCardType[], element: ElementEnum) => {
      const spellDlis = getSpells(deckCards);
      const spellPipsOfElement = getPipCount(spellDlis, element);
      const totalSpellPips = getPipCount(spellDlis);
      return Math.trunc((spellPipsOfElement / totalSpellPips || 0) * 100);
    },
    [getPipCount, getSpells],
  );

  const sitePipRatio = useCallback(
    (deckCards: DeckCardType[], element: ElementEnum) => {
      const siteDlis = getSites(deckCards);
      const sitePipsOfElement = getPipCount(siteDlis, element);
      const totalSitePips = getPipCount(siteDlis);
      return Math.trunc((sitePipsOfElement / totalSitePips || 0) * 100);
    },
    [getPipCount, getSites],
  );

  const curveData = useMemo(() => {
    const spells = getSpells(decklist);
    const manaCosts = spells.map((spell) => spell.card.guardian?.cost ?? 0);
    const maxManaCost = Math.max(...manaCosts);
    const curveData = [];
    for (let i = 0; i <= maxManaCost; i++) {
      // overall count of everythiung
      const count = spells.reduce((a, c) => {
        if (c.card.guardian?.cost === i) {
          return a + c.quantity;
        }
        return a;
      }, 0);
      // earthCount = cards with !!earthThreshold  && earthThreshold > 0
      const earthCount = spells.reduce((a, c) => {
        if (c.card.guardian?.cost === i && !!elementToThreshold(c, ElementEnum.Earth)) {
          return a + c.quantity;
        }
        return a;
      }, 0);
      ("");
      // fireCount = cards with !!fireThreshold  && fireThreshold > 0
      const fireCount = spells.reduce((a, c) => {
        if (c.card.guardian?.cost === i && !!elementToThreshold(c, ElementEnum.Fire)) {
          return a + c.quantity;
        }
        return a;
      }, 0);
      // waterCount = cards with !!waterThreshold  && waterThreshold > 0
      const waterCount = spells.reduce((a, c) => {
        if (c.card.guardian?.cost === i && !!elementToThreshold(c, ElementEnum.Water)) {
          return a + c.quantity;
        }
        return a;
      }, 0);
      // airCount = cards with !!airThreshold  && airThreshold > 0
      const airCount = spells.reduce((a, c) => {
        if (c.card.guardian?.cost === i && !!elementToThreshold(c, ElementEnum.Air)) {
          return a + c.quantity;
        }
        return a;
      }, 0);

      curveData.push({
        cost: i,
        count,
        earthCount,
        fireCount,
        waterCount,
        airCount,
      });
    }
    return curveData;
  }, [decklist, getSpells]);

  return (
    <div className={cn("flex flex-col justify-center", size === "lg" && "mb-12 items-center")}>
      <div className={cn("flex flex-row flex-wrap ", size === "lg" && "mb-12 justify-center")}>
        {[ElementEnum.Earth, ElementEnum.Fire, ElementEnum.Water, ElementEnum.Air].map(
          (element) => (
            <div key={element} className={!hasCardsWithPip(decklist, element) ? "opacity-40" : ""}>
              {size === "lg" ? (
                <div className="mx-1 flex flex-col items-center sm:mx-4">
                  <Image
                    src={elementToImgMap[element]}
                    className="my-1 h-[25px] w-[25px] md:h-[40px] md:w-[40px]"
                    alt=""
                  />

                  <H3 className="!mb-0 -mt-2 !text-lg md:!text-xl">
                    {spellPipRatio(decklist, element)}% cost
                  </H3>

                  <div className="-mt-1 opacity-80">
                    {sitePipRatio(decklist, element)}% resource
                  </div>
                </div>
              ) : (
                <div className="mr-2 flex flex-row items-center ">
                  <Image
                    src={elementToImgMap[element]}
                    className="mr-1 mt-0.5 h-[15px] w-[15px]"
                    alt=""
                  />
                  {spellPipRatio(decklist, element)}
                  <span>% /</span>
                  &nbsp;
                  {sitePipRatio(decklist, element)}
                  <span> %</span>
                </div>
              )}
            </div>
          ),
        )}
      </div>

      {/* Dont render on SSR due to recharts being shit */}
      {showManaCurve && <ManaCurveChart data={curveData} />}
    </div>
  );
};

const eMap = {
  [ElementEnum.Earth]: "earthThreshold",
  [ElementEnum.Fire]: "fireThreshold",
  [ElementEnum.Water]: "waterThreshold",
  [ElementEnum.Air]: "airThreshold",
};
const elementToThreshold = (c: DeckCardType, e: ElementEnum) =>
  // @ts-expect-error 'none' doesnt appear in the emap, so it gets confused
  (c.card.guardian[eMap[e]] ?? 0) as number;
